import { auth } from "@fynd-onshelf/contracts";
import { zodResolver } from "@hookform/resolvers/zod";
import type { JSX } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useSearchParams } from "react-router";
import { z } from "zod";

import { ErrorState } from "../components/data/index.js";
import { Button } from "../components/ui/button.js";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card.js";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../components/ui/form.js";
import { Input } from "../components/ui/input.js";
import { useResetPasswordMutation } from "../lib/api/auth.js";
import { APIError } from "../lib/http.js";

const schema = z
  .object({
    newPassword: auth.passwordSchema,
    confirmPassword: z.string(),
  })
  .superRefine((v, ctx) => {
    if (v.newPassword.trim() !== v.confirmPassword.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["confirmPassword"],
        message: "Passwords do not match",
      });
    }
  });
type Values = z.infer<typeof schema>;

export function ResetPasswordPage(): JSX.Element {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const mut = useResetPasswordMutation({
    onSuccess: () => {
      void navigate("/login?message=password-reset", { replace: true });
    },
  });
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { newPassword: "", confirmPassword: "" },
  });

  if (!token) {
    return (
      <main className="flex h-full items-center justify-center bg-background p-6">
        <Card className="w-full max-w-sm">
          <CardHeader>
            <CardTitle>Reset password</CardTitle>
          </CardHeader>
          <CardContent>
            <ErrorState error={new Error("Missing reset token in URL")} />
            <Link
              to="/forgot-password"
              className="mt-4 block text-center text-sm text-primary hover:underline"
            >
              Request a new reset link
            </Link>
          </CardContent>
        </Card>
      </main>
    );
  }

  const submitError = mut.error
    ? mut.error instanceof APIError
      ? mut.error.displayDetail
      : (mut.error as Error).message
    : null;

  return (
    <main className="flex h-full items-center justify-center bg-background p-6">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>Set a new password</CardTitle>
          <CardDescription>This will sign you out everywhere.</CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit((v) => mut.mutate({ token, newPassword: v.newPassword }))}
              className="space-y-3"
              noValidate
            >
              <FormField
                control={form.control}
                name="newPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>New password</FormLabel>
                    <FormControl>
                      <Input type="password" autoComplete="new-password" autoFocus {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirm</FormLabel>
                    <FormControl>
                      <Input type="password" autoComplete="new-password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {submitError ? <p className="text-sm text-destructive">{submitError}</p> : null}
              <Button type="submit" className="w-full" disabled={mut.isPending}>
                {mut.isPending ? "Resetting…" : "Reset password"}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>
    </main>
  );
}
